import React, { memo, useMemo } from 'react'

import { Icon, RotatingSpinner, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  bool, func, number, object, shape, string,
} from 'prop-types'

import { iconSizeXLarge } from 'Pods/ThreeDStudio/constants'

const spinner = <Icon svgComponent={RotatingSpinner} color="light" size={iconSizeXLarge} notSpaced />

// Largest the center +/- glyph is ever drawn; on small brushes it shrinks so it
// always fits inside the ring (see buildMaskCursor).
const GLYPH_MAX = 12
// Below this brush size the +/- glyph no longer fits legibly, so the cursor
// falls back to a plain dot (see buildMaskCursor).
const GLYPH_MIN_BRUSH = 9
// Breathing room around the ring so its stroke and dark outline are not clipped.
const CURSOR_PADDING = 4

// Builds the painting cursor as an inline SVG data-URI so the ring scales 1:1
// with the brush (canvas px map 1:1 to CSS px). Each shape is drawn twice: a
// wider semi-transparent dark stroke underneath acts as an outline so the white
// stroke on top reads on both light and dark image regions. We deliberately
// avoid an SVG <filter>/drop-shadow here — filters force the whole cursor to be
// rasterized at 1x and then upscaled on HiDPI screens, which looks pixelated;
// pure vector strokes stay crisp at any device pixel ratio. From GLYPH_MIN_BRUSH
// up the center glyph is a "+" while painting and a "-" while erasing; below it
// the cursor is a plain dot — a filled white circle while painting, an empty
// white ring while erasing. The hotspot sits dead center so the ring matches the
// stroke the brush will lay down.
function buildMaskCursor(brushSize, isErasing) {
  const size = brushSize + CURSOR_PADDING * 2
  const center = size / 2
  const radius = brushSize / 2
  const circle = `<circle cx="${center}" cy="${center}" r="${radius}"/>`

  let shapes
  let fill = 'none'
  if (brushSize < GLYPH_MIN_BRUSH) {
    shapes = circle
    fill = isErasing ? 'none' : '#ffffff'
  } else {
    const glyph = Math.min(GLYPH_MAX, Math.round(brushSize * 0.55))
    const half = glyph / 2
    const vertical = isErasing
      ? ''
      : `<line x1="${center}" y1="${center - half}" x2="${center}" y2="${center + half}"/>`
    shapes = [
      circle,
      `<line x1="${center - half}" y1="${center}" x2="${center + half}" y2="${center}"/>`,
      vertical,
    ].join('')
  }

  // The eraser reads as a lighter tool, so its ring gets a thinner border.
  const stroke = isErasing ? 1 : 1.5
  const outline = stroke * 2
  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">`,
    '<g stroke-linecap="round">',
    `<g fill="none" stroke="#000000" stroke-opacity="0.45" stroke-width="${outline}">${shapes}</g>`,
    `<g fill="${fill}" stroke="#ffffff" stroke-width="${stroke}">${shapes}</g>`,
    '</g></svg>',
  ].join('')
  const hotspot = Math.round(center)
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") ${hotspot} ${hotspot}, crosshair`
}

// The base image sits underneath a transparent <canvas> the user paints the mask
// onto. Both share the card's 86/56 box so canvas pixels map 1:1 to the image.
// While an edit is in flight a dimming overlay covers the stage: it shows a
// spinner and, by capturing pointer events, blocks further painting.
function MaskCanvas({
  image,
  canvasRef,
  containerRef,
  brushSize,
  isErasing,
  isSending,
  onPointerDown,
  onPointerMove,
  onPointerUp,
}) {
  const cursor = useMemo(() => buildMaskCursor(brushSize, isErasing), [brushSize, isErasing])

  return (
    <div className="image-assistant-edit-tab__canvas-stage" ref={containerRef} aria-busy={isSending}>
      <img className="image-assistant-edit-tab__canvas-image" src={image.url} alt={image.alt || ''} />
      <canvas
        ref={canvasRef}
        className={classnames(
          'image-assistant-edit-tab__canvas-mask',
          isErasing && 'image-assistant-edit-tab__canvas-mask--erasing',
        )}
        style={{ cursor }}
        aria-label="Paint a mask over the area to edit"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
      />
      {isSending && (
        <div className="image-assistant-edit-tab__canvas-overlay" role="status" aria-label="Processing edit">
          {spinner}
        </div>
      )}
    </div>
  )
}

MaskCanvas.propTypes = {
  image: shape({ url: string.isRequired, alt: string }).isRequired,
  canvasRef: shape({ current: object }).isRequired,
  containerRef: shape({ current: object }).isRequired,
  brushSize: number.isRequired,
  isErasing: bool.isRequired,
  isSending: bool.isRequired,
  onPointerDown: func.isRequired,
  onPointerMove: func.isRequired,
  onPointerUp: func.isRequired,
}

export default memo(MaskCanvas)
